export const getValue = (source, pascalCaseKey, camelCaseKey) => {
    if (!source || typeof source !== "object") {
        return undefined;
    }

    return source[pascalCaseKey] ?? source[camelCaseKey];
};

export const normalizeApiError = (rawError, status = null) => {
    if (!rawError || typeof rawError !== "object") {
        return null;
    }

    return {
        code: getValue(rawError, "Code", "code") ?? "unknown_error",
        message: getValue(rawError, "Message", "message") ?? "Unexpected error.",
        details: getValue(rawError, "Details", "details") ?? null,
        httpStatus: getValue(rawError, "HttpStatus", "httpStatus") ?? status,
    };
};

export const normalizeEnvelope = (payload, { defaultSuccessForNonEnvelope = true } = {}) => {
    if (!payload || typeof payload !== "object") {
        return {
            success: defaultSuccessForNonEnvelope,
            data: payload ?? null,
            error: null,
        };
    }

    const hasEnvelopeShape =
        Object.prototype.hasOwnProperty.call(payload, "Success") ||
        Object.prototype.hasOwnProperty.call(payload, "success") ||
        Object.prototype.hasOwnProperty.call(payload, "Data") ||
        Object.prototype.hasOwnProperty.call(payload, "data") ||
        Object.prototype.hasOwnProperty.call(payload, "Error") ||
        Object.prototype.hasOwnProperty.call(payload, "error");

    if (!hasEnvelopeShape) {
        return {
            success: defaultSuccessForNonEnvelope,
            data: payload,
            error: null,
        };
    }

    return {
        success: Boolean(getValue(payload, "Success", "success")),
        data: getValue(payload, "Data", "data") ?? null,
        error: normalizeApiError(getValue(payload, "Error", "error")),
    };
};

export const getEnvelopeDataOrThrow = (payload, fallbackMessage) => {
    const envelope = normalizeEnvelope(payload);
    if (!envelope.success) {
        const errorMessage = envelope.error?.message ?? envelope.error?.code ?? fallbackMessage;

        throw new Error(String(errorMessage || fallbackMessage));
    }

    return envelope.data;
};

export const buildFailureResult = (error, fallbackMessage, { fallbackCode = "request_failed" } = {}) => {
    const responseStatus = error?.response?.status ?? null;
    const responsePayload = error?.response?.data;

    if (responsePayload) {
        const envelope = normalizeEnvelope(responsePayload, { defaultSuccessForNonEnvelope: false });
        if (envelope.error) {
            return {
                success: false,
                data: null,
                error: envelope.error,
            };
        }

        const directError = normalizeApiError(responsePayload, responseStatus);
        if (directError) {
            return {
                success: false,
                data: null,
                error: directError,
            };
        }
    }

    return {
        success: false,
        data: null,
        error: {
            code: fallbackCode,
            message: fallbackMessage,
            details: null,
            httpStatus: responseStatus,
        },
    };
};

export const createHttpClient = (axiosClient, { baseURL = "", getAuthToken } = {}) => {
    if (!axiosClient || typeof axiosClient.create !== "function") {
        throw new Error("createHttpClient requires a valid axios instance.");
    }

    const httpClient = axiosClient.create({
        baseURL,
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (typeof getAuthToken === "function") {
        httpClient.interceptors.request.use((config) => {
            const token = getAuthToken();

            if (token) {
                config.headers = config.headers ?? {};
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        });
    }

    return httpClient;
};
